/**
 * Tela de login do Terminal Kiosk — Produção v2 (P3, corte único).
 *
 * Fluxo:
 *   1. Na instalação, o gestor digita uma vez o número do posto que
 *      este tablet atende (`POSTO-UN1-3`, impresso na estação). Fica
 *      gravado no aparelho.
 *   2. No dia a dia o operador digita só o PIN (4-6 dígitos).
 *   3. POST /api/producao/terminal/login {pin, posto}
 *      -> sessão (token + posto + etapa) em sessionStorage
 *   4. Navega para /producao/terminal/bipar
 *
 * Antes desta versão a tela abria com dois dropdowns alimentados por
 * `GET terminal/unidades` e `GET terminal/postos` — ambos sem token, ou
 * seja, qualquer visitante da API baixava a lista de todas as
 * marcenarias clientes. Os endpoints foram removidos: o número do posto
 * já é único e já embute a unidade, então o dropdown nunca foi
 * necessário para o login funcionar.
 *
 * Sem AuthContext (terminal não usa JWT do RBAC).
 */

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Alert,
  FieldLabel,
  FieldRow,
  Input,
  KioskBody,
  KioskCard,
  PrimaryButton,
  Spacer,
  Subtitle,
  Title,
} from "./styles";
import {
  lerPostoConfigurado,
  limparPostoConfigurado,
  login,
  salvarPostoConfigurado,
} from "./client";
import { useSomFeedback } from "./useSomFeedback";

export default function TerminalLogin() {
  const navigate = useNavigate();
  const { tocarOk, tocarErro } = useSomFeedback();

  const postoGravado = lerPostoConfigurado();
  const [posto, setPosto] = useState(postoGravado);
  // Aparelho já configurado entra direto no PIN; o campo do posto só
  // reaparece quando o gestor pede para trocar.
  const [editandoPosto, setEditandoPosto] = useState(!postoGravado);
  const [pin, setPin] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState("");
  const [bloqueadoAte, setBloqueadoAte] = useState(null);

  const podeEnviar = posto.trim() && /^\d{4,6}$/.test(pin) && !enviando;

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!podeEnviar) return;
    setErro("");
    setBloqueadoAte(null);
    setEnviando(true);
    try {
      const sessao = await login({ pin, posto: posto.trim() });
      // Guarda o número canônico devolvido pelo servidor (não o que foi
      // digitado) para não gravar variação de caixa no aparelho.
      salvarPostoConfigurado(sessao?.posto?.numero || posto.trim());
      tocarOk();
      navigate("/producao/terminal/bipar");
    } catch (err) {
      tocarErro();
      const payload = err?.payload || {};
      if (err.status === 423 && payload.bloqueado_ate) {
        setBloqueadoAte(payload.bloqueado_ate);
        setErro(
          "Operador bloqueado por excesso de tentativas. Contate o gestor.",
        );
      } else {
        setErro(err.message || "PIN não reconhecido.");
      }
      setPin("");
    } finally {
      setEnviando(false);
    }
  }

  function trocarPosto() {
    limparPostoConfigurado();
    setEditandoPosto(true);
    setErro("");
  }

  return (
    <KioskBody>
      <KioskCard>
        <Title>Terminal de Bipagem</Title>
        <Subtitle>
          {editandoPosto
            ? "Configure o posto deste terminal"
            : "Entre com seu PIN"}
        </Subtitle>

        {erro && (
          <Alert $tone={bloqueadoAte ? "danger" : "warning"}>
            <span>{erro}</span>
          </Alert>
        )}

        {bloqueadoAte && (
          <Alert $tone="info">
            <span>
              Bloqueado até{" "}
              {new Date(bloqueadoAte).toLocaleTimeString("pt-BR", {
                hour: "2-digit",
                minute: "2-digit",
              })}
            </span>
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          {editandoPosto ? (
            <FieldRow>
              <FieldLabel>Posto (código impresso na estação)</FieldLabel>
              <Input
                type="text"
                autoComplete="off"
                autoFocus
                value={posto}
                placeholder="POSTO-UN1-1"
                onChange={(e) => setPosto(e.target.value.toUpperCase())}
                disabled={enviando}
              />
            </FieldRow>
          ) : (
            <FieldRow>
              <FieldLabel>Posto</FieldLabel>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 12,
                }}
              >
                <strong style={{ fontSize: 22, letterSpacing: ".04em" }}>
                  {posto}
                </strong>
                <button
                  type="button"
                  onClick={trocarPosto}
                  disabled={enviando}
                  style={{
                    background: "none",
                    border: "none",
                    color: "inherit",
                    opacity: 0.7,
                    textDecoration: "underline",
                    cursor: "pointer",
                    fontSize: 16,
                    minHeight: 48,
                    padding: "0 8px",
                  }}
                >
                  trocar
                </button>
              </div>
            </FieldRow>
          )}

          <FieldRow>
            <FieldLabel>PIN (4 a 6 dígitos)</FieldLabel>
            <Input
              type="password"
              inputMode="numeric"
              autoComplete="off"
              autoFocus={!editandoPosto}
              maxLength={6}
              value={pin}
              placeholder="****"
              onChange={(e) => setPin(e.target.value.replace(/\D/g, ""))}
              disabled={enviando}
            />
          </FieldRow>

          <Spacer $h={8} />

          <PrimaryButton type="submit" disabled={!podeEnviar}>
            {enviando ? "Entrando..." : "Entrar"}
          </PrimaryButton>
        </form>
      </KioskCard>
    </KioskBody>
  );
}
